import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { authenticatePasskey, passkeysSupported } from '../passkey';
import { authUrl, returnPathFor } from '../utils/authReturn';
import './Auth.css';

function SignIn() {
  const { user, login, applySession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where to go once signed in: the page that sent the person here (validated),
  // or home. `replace` so Back doesn't return to a sign-in page that would just
  // redirect again.
  const destination = returnPathFor(location);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    document.title = 'Sign In - Brookline Notepad';
  }, []);

  // Already signed in (including the moment a login succeeds): go back to the
  // page that sent them. This must use the same destination as the handlers
  // below, since it fires as soon as `user` is set and would otherwise win.
  useEffect(() => {
    if (user) {
      navigate(destination, { replace: true });
    }
  }, [user, navigate, destination]);

  useEffect(() => {
    if (loginError && errorRef.current) errorRef.current.focus();
  }, [loginError]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);
    try {
      await login(email, password);
      navigate(destination, { replace: true });
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoggingIn(false);
    }
  };

  const handlePasskey = async () => {
    setLoginError('');
    setPasskeyBusy(true);
    try {
      const data = await authenticatePasskey(email);
      applySession(data);
      navigate(destination, { replace: true });
    } catch (err) {
      setLoginError(err.message || 'Passkey sign-in failed.');
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotMsg('');
    setForgotSending(true);
    try {
      const res = await fetch('/api/auth/request-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      setForgotMsg(data.message || 'If an account with that email exists, a reset link has been sent.');
    } catch (err) {
      setForgotMsg('Network error. Please try again.');
    } finally {
      setForgotSending(false);
    }
  };

  return (
    <div className="auth-page">
      <a href="#main-content" className="auth-skip-link">Skip to main content</a>
      <Header title="Brookline Notepad" />

      <main id="main-content" tabIndex={-1} className="auth-content">
        <div className="auth-card">
          <h2 className="auth-title">Sign In</h2>
          <p className="auth-subtitle">Sign in to access additional pages and resources.</p>

          <form onSubmit={handleLogin} className="auth-form">
            <div className="auth-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
              />
            </div>
            <div className="auth-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {loginError && <div className="auth-error" role="alert" tabIndex={-1} ref={errorRef}>{loginError}</div>}

            <button type="submit" className="auth-btn" disabled={loggingIn}>
              {loggingIn ? 'Signing in...' : 'Sign In'}
            </button>

            <button type="button" className="auth-forgot-link" onClick={() => setShowForgot(!showForgot)}>
              Forgot password?
            </button>
          </form>

          {passkeysSupported() && (
            <>
              <div className="auth-divider">or</div>
              <button type="button" className="auth-btn-secondary" onClick={handlePasskey} disabled={passkeyBusy}>
                {passkeyBusy ? 'Waiting for your device…' : 'Sign in with a passkey'}
              </button>
            </>
          )}

          {showForgot && (
            <div className="forgot-password-form">
              <p>Enter your email to receive a password reset link.</p>
              <form onSubmit={handleForgot} className="auth-form">
                <div className="auth-field">
                  <label htmlFor="forgot-email">Email</label>
                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                  />
                </div>
                {forgotMsg && <div className="auth-success" role="status">{forgotMsg}</div>}
                <button type="submit" className="auth-btn" disabled={forgotSending}>
                  {forgotSending ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            </div>
          )}

          <button className="auth-link-btn" onClick={() => navigate(authUrl('/signup', location))}>
            New to Brookline Notepad? Create an account.
          </button>
          <br />
          <button className="auth-link-btn" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
        </div>
      </main>
    </div>
  );
}

export default SignIn;
